import { useState, useRef, useEffect, useMemo, useId } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  searchGlobal, isGlobalSearchAvailable, MIN_QUERY_LENGTH,
} from "../api/globalSearch";

/**
 * THE ONE GLOBAL SEARCH CONTROL (Part 1 §3B, §4).
 *
 * It lives in the header, so there is ONE of it for the whole application and no page carries a
 * second copy. It does not replace anything: `pages/salesPayment/SalesSearch.jsx` and every embedded
 * list search — Properties, Customers, Legal Cases, Reports, Documents — are untouched and still do
 * what they always did. The final architecture is GLOBAL SEARCH **plus** PAGE-SPECIFIC SEARCH.
 *
 * ── IT NEVER FILTERS FOR AUTHORIZATION (§3) ─────────────────────────────────
 * Nothing in this file inspects the user, their department or their designation, and nothing here
 * drops a row. It cannot: `searchGlobal` sends the query text and nothing else, so what comes back
 * is already exactly what the server decided this caller may find. A component that filtered results
 * would be the client-side security §3 rules out — and would also be defeated by anyone opening the
 * network tab.
 *
 * ── EVERY STATE IS NAMED, AND NONE OF THEM IS A BLANK BOX (§3B, §18) ────────
 *   IDLE         placeholder only, no panel
 *   TOO SHORT    "Type at least 2 characters."
 *   LOADING      "Searching…"
 *   RESULTS      grouped, each row a real destination
 *   EMPTY        No results found for "…"
 *   ERROR        Search couldn't be completed. + Retry
 *   UNAVAILABLE  the backend prerequisite, stated plainly, with NO Retry
 *
 * The last one is the state the application is actually in today, and it is deliberately distinct
 * from ERROR. A missing endpoint is not a transient failure, so offering "Retry" would promise
 * something no number of retries can deliver. See `api/globalSearch.js` for the prerequisite.
 *
 * ── KEYBOARD (§4) ───────────────────────────────────────────────────────────
 * Tab reaches the input in normal document order — focus is never trapped and never stolen.
 * Ctrl/Cmd + K focuses it from anywhere; the repository contained no keyboard shortcut of any kind
 * before this (a search for `metaKey`/`ctrlKey` across both frontends returned nothing), so nothing
 * is being shadowed. ArrowDown/ArrowUp walk the flattened result list, Enter opens the highlighted
 * row, Escape closes the panel and leaves focus where it was.
 *
 * ── ARIA: THE COMBOBOX PATTERN, AND NOT ONE ATTRIBUTE MORE (§4, §17) ────────
 * The input is a `combobox` controlling a `listbox`; the highlighted row is pointed at by
 * `aria-activedescendant` so focus itself never leaves the input. Rows are `option`s rather than
 * links precisely BECAUSE the pattern requires it — navigation happens in the handler. The clear
 * button and the retry button are ordinary buttons with real labels and carry no ARIA at all.
 */
export default function GlobalSearch({ onNavigate }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const wrapRef = useRef(null);

  // `useId` rather than a hard-coded id: the header renders once today, but an id collision is the
  // kind of bug that only appears the day a second instance is mounted, and by then the accessible
  // name is silently pointing at the wrong panel.
  const uid = useId();
  const panelId = `global-search-panel-${uid}`;
  const inputId = `global-search-input-${uid}`;
  const optionId = (index) => `global-search-option-${uid}-${index}`;

  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);

  const q = text.trim();

  // ── THE HIGHLIGHT IS STORED WITH THE QUERY IT BELONGS TO ──────────────────
  // A new query must invalidate the old highlight: without that, ArrowDown → retype → Enter opens
  // whatever now happens to sit at the remembered index — a different record entirely.
  //
  // The obvious way to write that is `useEffect(() => setActive(-1), [q])`, and it is the wrong way.
  // It resets AFTER a render has already happened with a stale index, and `react-hooks` v7 flags it
  // as the cascading-render it is. Keeping the query beside the index instead makes the reset
  // DERIVED — there is no window in which the two disagree, and no effect at all.
  const [highlight, setHighlight] = useState({ q: "", index: -1 });
  const active = highlight.q === q ? highlight.index : -1;
  const setActive = (next) =>
    setHighlight((prev) => ({
      q,
      index: typeof next === "function" ? next(prev.q === q ? prev.index : -1) : next,
    }));

  const longEnough = q.length >= MIN_QUERY_LENGTH;
  const available = isGlobalSearchAvailable();

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ["global-search", q],
    queryFn: () => searchGlobal(q),
    // Never fires while the endpoint is absent, so the panel's UNAVAILABLE state costs zero requests
    // and no 404 is ever produced to be mistaken for a defect.
    enabled: available && longEnough,
    staleTime: 30 * 1000,
    // A search is a convenience; a failure should surface immediately as a state the reader can act
    // on, not after three silent retries that make the box feel broken.
    retry: false,
  });

  const groups = useMemo(() => (data?.groups || []).filter((g) => g.items?.length), [data]);

  // ONE FLAT LIST, so ArrowDown crosses a group boundary the way a reader expects rather than
  // stopping at the end of "Properties". The rendered rows read their index straight out of this,
  // so the highlighted row and the row Enter opens cannot drift apart.
  const flat = useMemo(
    () => groups.flatMap((g) => g.items.map((item) => ({ ...item, groupKey: g.key }))),
    [groups]
  );

  // ── Ctrl/Cmd + K ──────────────────────────────────────────────────────────
  // Bound to `document` because the point of the shortcut is that it works from anywhere.
  // `preventDefault` stops the browser's own Ctrl+K where one exists.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Clicking anywhere else dismisses the panel. Armed only while it is open, so the ordinary page
  // carries no document-level listener.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const unavailable = !available;
  const showPanel = open && q.length > 0;

  const go = (item) => {
    if (!item?.to) return;
    setOpen(false);
    setActive(-1);
    onNavigate?.();
    // A NORMAL in-application navigation. The destination's own `RoleRoute` runs and its own server
    // read re-checks the caller — a search result is a door, never a bypass (§3C).
    navigate(item.to);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      // Closes the panel and LEAVES FOCUS ON THE INPUT. Moving focus elsewhere on Escape is the
      // behaviour that loses a keyboard user their place.
      setOpen(false);
      setActive(-1);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (flat.length) setActive((i) => (i + 1) % flat.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (flat.length) setActive((i) => (i <= 0 ? flat.length - 1 : i - 1));
      return;
    }
    if (e.key === "Enter") {
      if (active >= 0 && flat[active]) {
        e.preventDefault();
        go(flat[active]);
      }
      // With nothing highlighted, Enter does nothing rather than guessing at the first row: §3D
      // rules out a results PAGE for now (no approved route), so there is no "see all" to submit to.
    }
  };

  return (
    <div className="global-search" ref={wrapRef}>
      <div className="input-group input-group-sm global-search-box">
        <span className="input-group-text bg-white border-end-0">
          <i className="bi bi-search" aria-hidden="true" />
        </span>
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          className="form-control border-start-0"
          placeholder="Search VVR…"
          aria-label="Search VVR"
          value={text}
          autoComplete="off"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={panelId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 && flat[active] ? optionId(active) : undefined}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => { if (text) setOpen(true); }}
          onKeyDown={onKeyDown}
        />
        {text && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            aria-label="Clear search"
            onClick={() => { setText(""); setOpen(false); setActive(-1); inputRef.current?.focus(); }}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        )}
      </div>

      {showPanel && (
        <div id={panelId} ref={panelRef} className="global-search-panel" role="listbox" aria-label="Search results">
          {/* Every branch below renders TEXT. There is no path through this panel that produces an
              empty container, which is §18's actual requirement. */}
          {!longEnough ? (
            <div className="gs-msg">Type at least {MIN_QUERY_LENGTH} characters.</div>
          ) : unavailable ? (
            <div className="gs-msg">
              <div className="fw-semibold text-body">Global search is not available yet.</div>
              <div className="small">
                No authorized global-search endpoint exists on the server, so nothing is shown here.
                Use the search on an individual list — Properties, Customers, Legal Cases, Reports —
                in the meantime.
              </div>
              {/* NO RETRY BUTTON. A missing prerequisite is not a transient failure. */}
            </div>
          ) : isFetching && groups.length === 0 ? (
            <div className="gs-msg">Searching…</div>
          ) : isError ? (
            <div className="gs-msg">
              <div className="text-body">Search couldn’t be completed.</div>
              {error?.message && <div className="small">{error.message}</div>}
              <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          ) : flat.length === 0 ? (
            <div className="gs-msg">
              No results found for “{q}”.
              {/* Mirrors what SalesSearch already says: a thin result set may be a scope, not an
                  absence, and saying so stops a reader concluding the record is missing. */}
              {data?.scope === "own" && (
                <div className="small">You see your own records and your own work here.</div>
              )}
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="gs-group">
                <div className="gs-group-label">{group.label}</div>
                {group.items.map((item) => {
                  const index = flat.findIndex(
                    (f) => f.groupKey === group.key && String(f.id) === String(item.id)
                  );
                  return (
                    <div
                      key={`${group.key}-${item.id}`}
                      id={optionId(index)}
                      role="option"
                      aria-selected={index === active}
                      className={`gs-item${index === active ? " active" : ""}`}
                      // `onMouseDown`, not `onClick`: the input's blur would otherwise close the
                      // panel before the click lands on a row that no longer exists.
                      onMouseDown={(e) => { e.preventDefault(); go(item); }}
                      onMouseEnter={() => setActive(index)}
                    >
                      {item.code && <span className="gs-code">{item.code}</span>}
                      <span className="gs-label">{item.label}</span>
                      {item.detail && <span className="gs-detail">{item.detail}</span>}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
